<template>
    <div>
      <!-- Botão de Sino -->
      <v-btn @click="toggleNotifications" icon class="notification-btn" v-tooltip="'Notificações'">
        <v-badge v-if="unreadCount > 0" color="red" :content="unreadCount > 99 ? '99+' : unreadCount" overlap>
           <v-icon>mdi-bell</v-icon>
        </v-badge>
        <v-icon v-else>mdi-bell-outline</v-icon>
      </v-btn>
  
      <!-- Dropdown de Notificações -->
      <v-menu
        v-model="showNotifications"
        :position-x="x"
        :position-y="y"
        offset-y
        :close-on-content-click="false"
        transition="scale-transition"
        origin="top right"
      >
        <v-card class="notification-card" :class="{ 'dark-theme': isDark }" :style="backgroundTheme">
          <v-toolbar dense flat class="filter-toolbar">
             <v-toolbar-title class="text-subtitle-1">Notificações</v-toolbar-title>
             <v-spacer></v-spacer>
             <v-progress-circular v-if="loading" indeterminate size="20" width="2" color="primary"></v-progress-circular>
          </v-toolbar>

          <!-- Abas de status -->
          <v-tabs v-model="activeTab" background-color="transparent" color="primary" centered dense class="tab-container" @change="fetchNotifications">
            <v-tab value="unread">
              <v-badge color="red" :content="unreadCount > 0 ? unreadCount : ''" :value="unreadCount > 0" offset-x="-3" offset-y="10">
                Não lidas
              </v-badge>
            </v-tab>
            <v-tab value="read-today">
              <v-badge color="blue" :content="readTodayCount > 0 ? readTodayCount : ''" :value="readTodayCount > 0" offset-x="-3" offset-y="10">
                Lidas hoje
              </v-badge>
            </v-tab>
          </v-tabs>

          <!-- Filtros -->
          <v-toolbar dense class="filter-toolbar">
            <div class="filter-scroll-container">
              <v-btn text small @click="filterByCategory('Todos')" :class="{ active: activeFilter === 'Todos' }">
                Todos
              </v-btn>
              <v-btn text small @click="filterByCategory('proposta')" :class="{ active: activeFilter === 'proposta' }">
                Proposta
              </v-btn>
              <v-btn text small @click="filterByCategory('lista_tecnica')" :class="{ active: activeFilter === 'lista_tecnica' }">
                Lista Técnica
              </v-btn>
              <v-btn text small @click="filterByCategory('checklist')" :class="{ active: activeFilter === 'checklist' }">
                Checklist
              </v-btn>
              <v-btn text small @click="filterByCategory('comunicado')" :class="{ active: activeFilter === 'comunicado' }">
                Comunicado
              </v-btn>
            </div>
          </v-toolbar>
          <v-toolbar dense class="urgency-toolbar">
             <div class="filter-scroll-container">
                <v-btn text small @click="filterByUrgency('Todas')" :class="{ active: activeUrgencyFilter === 'Todas' }">
                  Todas
                </v-btn>
                <v-btn text small @click="filterByUrgency('Urgente')" :class="{ active: activeUrgencyFilter === 'Urgente' }">
                  Urgente
                </v-btn>
                <v-btn text small @click="filterByUrgency('Importante')" :class="{ active: activeUrgencyFilter === 'Importante' }">
                  Importante
                </v-btn>
                <v-btn text small @click="filterByUrgency('Normal')" :class="{ active: activeUrgencyFilter === 'Normal' }">
                  Normal
                </v-btn>
             </div>
           </v-toolbar>
  
           <!-- Mensagem de Erro -->
           <v-alert v-if="error" type="error" dense text class="ma-2">
             {{ error }}
           </v-alert>

           <!-- Mensagem de Nenhuma Notificação -->
           <v-list-item v-if="!loading && filteredNotifications.length === 0 && !error">
             <v-list-item-content class="text-center">
               <v-icon large small color="grey lighten-1">mdi-bell-off-outline</v-icon>
               <v-list-item-subtitle class="mt-2 grey--text">Nenhuma notificação encontrada.</v-list-item-subtitle>
             </v-list-item-content>
           </v-list-item>

          <!-- Lista de Notificações -->
          <v-list v-else-if="!loading && !error">
            <transition-group name="fade">
              <v-list-item
                v-for="(notification) in filteredNotifications"
                :key="notification.id"
                class="notification-item"
                :class="{ 'unread-item': !notification.read }"
                @mouseenter="handleMouseEnter(notification)"
                @click="handleItemClick(notification)"
              >
                <!-- Círculo azul para não lidas -->
                <div v-if="!notification.read" class="unread-indicator"></div>
                
                <!-- Ícone de notificação -->
                <v-list-item-icon class="mr-3">
                  <v-icon :color="getCategoryColor(notification.category)">
                    {{ getCategoryIcon(notification.category) }}
                  </v-icon>
                </v-list-item-icon>
  
                <!-- Conteúdo da Notificação -->
                <v-list-item-content>
                  <v-list-item-title class="notification-title">
                     <!-- Link removido do título para o item inteiro ser clicável -->
                     {{ formatNotificationMessage(notification) }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="notification-time">
                     {{ formatRelativeTime(notification.created_at) }}
                  </v-list-item-subtitle>
                  <!-- Tag de Categoria e Prioridade -->
                  <div class="notification-tags mt-1">
                    <v-chip x-small :color="getCategoryColor(notification.category)" class="mr-1"> 
                      {{ getCategoryLabel(notification.category) }}
                    </v-chip>
                     <v-chip x-small :color="getPriorityColor(notification.priority)">
                       <v-icon x-small left>{{ getPriorityIcon(notification.priority) }}</v-icon>
                       {{ notification.priority }}
                    </v-chip>
                  </div>
                </v-list-item-content>
  
                <div class="read-status-icon-container">
                   <v-tooltip v-if="!isMobile" left content-class="readers-tooltip">
                     <template v-slot:activator="{ on, attrs }">
                       <v-icon
                         color="grey"
                         v-bind="attrs"
                         v-on="on"
                         @mouseenter="fetchReaders(notification.id)"
                       >
                         mdi-check-all
                       </v-icon>
                     </template>
                     <div class="pa-1" style="max-width: 250px;">
                        <p class="text-caption font-weight-medium mb-1">Lido por:</p>
                        <v-divider class="mb-1"></v-divider>
                        <div v-if="readersData[notification.id]?.loading" class="text-center py-1">
                          <v-progress-circular indeterminate size="16" width="2"></v-progress-circular>
                        </div>
                        <div v-else-if="readersData[notification.id]?.error" class="text-caption red--text text--lighten-1">
                          {{ readersData[notification.id].error }}
                        </div>
                        <div v-else-if="readersData[notification.id]?.readers?.length > 0">
                          <div v-for="reader in readersData[notification.id].readers" :key="reader.user_id" class="text-caption py-0 my-0">
                            {{ reader.user_id == authStore.user?.id_user ? 'VOCÊ' : reader.name }} - {{ reader.formattedReadAt }}
                          </div>
                        </div>
                        <div v-else class="text-caption">
                          Ninguém leu ainda.
                        </div>
                     </div>
                   </v-tooltip>

                   <v-menu
                      v-else
                      v-model="readersMenu[notification.id]"
                      :close-on-content-click="false"
                      offset-y
                      left
                    >
                      <template v-slot:activator="{ on: menu, attrs }">
                        <v-icon
                          color="grey"
                          v-bind="attrs"
                          v-on="menu"
                          @click.stop="fetchReaders(notification.id)"
                        >
                           mdi-check-all
                        </v-icon>
                      </template>
                      <v-card max-width="250">
                         <v-list-item-subtitle class="pa-2 pb-0 text-caption font-weight-medium">Lido por:</v-list-item-subtitle>
                         <v-divider class="mx-2 mb-1"></v-divider>
                         <div class="px-2 pb-1">
                            <div v-if="readersData[notification.id]?.loading" class="text-center py-1">
                                <v-progress-circular indeterminate size="16" width="2"></v-progress-circular>
                            </div>
                            <div v-else-if="readersData[notification.id]?.error" class="text-caption red--text text--lighten-1">
                              {{ readersData[notification.id].error }}
                            </div>
                            <div v-else-if="readersData[notification.id]?.readers?.length > 0">
                              <div v-for="reader in readersData[notification.id].readers" :key="reader.user_id" class="text-caption py-0 my-0">
                                {{ reader.user_id == authStore.user?.id_user ? 'VOCÊ' : reader.name }} - {{ reader.formattedReadAt }}
                              </div>
                            </div>
                            <div v-else class="text-caption">
                              Ninguém leu ainda.
                            </div>
                         </div>
                         <v-card-actions class="pt-0">
                            <v-spacer></v-spacer>
                            <v-btn text x-small @click="readersMenu[notification.id] = false">Fechar</v-btn>
                         </v-card-actions>
                      </v-card>
                    </v-menu>
                </div>

                <!-- Barra de progresso da leitura -->
                <div v-if="!notification.read && readingProgress[notification.id] !== undefined" 
                     class="reading-progress-container">
                  <div class="reading-progress-bar" 
                       :style="{ width: readingProgress[notification.id] + '%' }"></div>
                  <span class="reading-time-remaining">
                    {{ 10 - Math.floor(readingProgress[notification.id] / 10) }}s
                  </span>
                </div>

              </v-list-item>
            </transition-group>
          </v-list>
  
          <!-- Rodapé -->
          <v-card-actions class="notification-footer">
            <v-btn text small color="primary" @click.stop="markAllAsRead" :disabled="loading || unreadCount === 0">
              Marcar todas como lidas
            </v-btn>
            <v-spacer></v-spacer>
             <v-btn text small @click="closeMenu">
               Fechar
             </v-btn>
          </v-card-actions>
        </v-card>
      </v-menu>
    </div>
  </template>
  
  <script setup>
  import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
  import { useVuetify, useHttp, useIp, useError, useRouter, useSocket } from "@/composables";
  import { useAuthStore } from "@/stores";
  import moment from 'moment';
  import 'moment/locale/pt-br';

  moment.locale('pt-br');
  
  const vuetify = useVuetify();
  const http = useHttp();
  const ips = useIp();
  const { toast } = useError();
  const router = useRouter();
  const authStore = useAuthStore();
  const { socket } = useSocket();
  
  const showNotifications = ref(false);
  const x = ref(0);
  const y = ref(0);
  
  const activeTab = ref('unread');
  const activeFilter = ref('Todos');
  const activeUrgencyFilter = ref('Todas');

  const loading = ref(false);
  const error = ref(null);
  
  const notifications = ref([]);

  const readersData = ref({});
  const readersMenu = ref({});
  const isMobile = computed(() => vuetify.breakpoint.mobile);

  const readingTimers = ref({});
  const readingProgress = ref({});

  const isToday = (dateString) => {
    if (!dateString) return false;
    
    const today = moment().startOf('day');
    const date = moment(dateString).startOf('day');
    return date.isSame(today);
  };

  // --- WebSocket Handlers ---
  const handleNewNotification = (newNotification) => {
    
    const currentUserId = authStore.user?.id_user;
    if (!currentUserId) {
      return;
    }
    
    let isAuthorized = false;
    
    if (newNotification.authorizedUserIds && Array.isArray(newNotification.authorizedUserIds)) {
      const currentUserIdStr = String(currentUserId);
      const authorizedIdsStr = newNotification.authorizedUserIds.map(id => String(id));
      
      isAuthorized = authorizedIdsStr.includes(currentUserIdStr);
      
      if (!isAuthorized) {
        return;
      }
    } else {
      isAuthorized = true;
    }
    
    if (isAuthorized) {
      // Filtro para evitar notificação duplicada
      if (!notifications.value.some(n => n.id === newNotification.id)) {
        console.log(`Adicionando nova notificação ID: ${newNotification.id}`);
        notifications.value.unshift({
          ...newNotification,
          read: newNotification.read_status === 1 || newNotification.read_status === true,
          read_at: newNotification.read_at || (newNotification.read_status === 1 || newNotification.read_status === true ? new Date().toISOString() : null)
        });
      } else {
        console.warn(`Notificação duplicada bloqueada - ID: ${newNotification.id}`);
      }
      if (!readersMenu.value[newNotification.id]) {
        readersMenu.value[newNotification.id] = false;
      }
    }
  };

  const handleMarkReadAck = (ack) => {
    if (!ack.success) {
      console.error('Failed to mark notification as read:', ack.error);
      // toast.error('Falha ao marcar como lida via WebSocket.');
      const notificationIndex = notifications.value.findIndex(n => n.id === ack.notificationId);
      if (notificationIndex !== -1) {
        notifications.value[notificationIndex].read = false;
      }
    }
  };

  const handleMarkAllReadAck = (ack) => {
    if (!ack.success) {
      console.error('Failed to mark all notifications as read:', ack.error);
      // toast.error('Falha ao marcar todas como lidas via WebSocket.');
    }
  };

  const userRegistered = ref(false);
  const listenersRegistered = ref(false);
  
  const handleRegisterAck = (ack) => {
    if (ack.success) {
      console.log(`User ${ack.userId} successfully registered in websocket`);
      userRegistered.value = true;
    } else {
      console.error('Failed to register user in websocket:', ack.error);
      userRegistered.value = false;
    }
  };
  // --- End WebSocket Handlers ---

  const fetchNotifications = async () => {
    loading.value = true;
    error.value = null;
    try {
      // Prepara os parâmetros para a requisição
      let queryParams = {};
      
      // Se estiver na aba de lidas hoje, adiciona o filtro
      if (activeTab.value === 'read-today') {
        queryParams.readToday = 'true';
      }
      
      const res = await http.post(
        ips.ipSecurity + 'notifications/list', 
        {
          userId: authStore.user?.id_user,
        }, 
        { params: queryParams }
      );
      
      if (res && res.data && Array.isArray(res.data.rows)) {
         // Filtro anti-duplicidade para notificações vindas da API
         const uniqueNotificationsMap = new Map();
         
         res.data.rows.forEach(n => {
           if (!uniqueNotificationsMap.has(n.id)) {
             uniqueNotificationsMap.set(n.id, {
               ...n,
               read: n.read_status === 1 || n.read_status === true,
               read_at: n.read_at || null
             });
           }
         });
         
         notifications.value = Array.from(uniqueNotificationsMap.values());
         readersMenu.value = notifications.value.reduce((acc, n) => {
            acc[n.id] = false;
            return acc;
         }, {});
      } else {
         console.warn("Formato inesperado da resposta de notificações ou 'rows' não é um array:", res?.data);
         notifications.value = [];
         readersMenu.value = {};
      }
    } catch (err) {
      console.error("Erro ao buscar notificações:", err);
      
      if (err.response && err.response.status === 403) {
        error.value = 'Você não possui permissão para acessar as notificações.';
      } else {
        error.value = 'Falha ao carregar notificações.';
      }
      
      notifications.value = [];
      readersMenu.value = {};
    } finally {
      loading.value = false;
    }
  };
  
  const unreadCount = computed(() => {
    return notifications.value.filter((n) => !n.read).length;
  });
  
  const readTodayCount = computed(() => {
    return notifications.value.filter((n) => n.read && n.read_at && isToday(n.read_at)).length;
  });
  
  const isDark = computed(() => vuetify.theme.dark);

  const backgroundTheme = computed(() => ({
    backgroundColor: isDark.value ? '#1E1E1E !important' : '#FFFFFF !important'
  }));

  const filteredNotifications = computed(() => {
    let filtered = notifications.value;
   
    // Filtrar por status (lidas hoje ou não lidas)
    if (activeTab.value == '0' || activeTab.value == 'unread') {
      filtered = filtered.filter(n => !n.read);
    } else if (activeTab.value == '1' || activeTab.value == 'read-today') {
      filtered = filtered.filter(n => n.read && isToday(n.read_at));
    }
   
    // Filtrar por categoria
    if (activeFilter.value !== 'Todos') {
      filtered = filtered.filter((n) => {
        const notificationCategory = typeof n.category === 'string' ? n.category.trim().toLowerCase() : '';
        const filterCategory = activeFilter.value.trim().toLowerCase();
        return notificationCategory === filterCategory;
      });
    }
  
    // Filtrar por urgência
    if (activeUrgencyFilter.value !== 'Todas') {
      filtered = filtered.filter((n) => n.priority === activeUrgencyFilter.value);
    }
  
    return [...filtered].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  });

  const formatRelativeTime = (dateTime) => {
    return moment(dateTime).fromNow();
  };
  
  const toggleNotifications = async (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    x.value = rect.left;
    y.value = rect.bottom + window.scrollY;
    const opening = !showNotifications.value;
    showNotifications.value = !showNotifications.value;

    if (opening) {
       readersData.value = {};
       if (notifications.value.length === 0) {
          await fetchNotifications();
       }

       setTimeout(() => {
          const visibleUnreadIds = filteredNotifications.value
              .filter(n => !n.read)
              .map(n => n.id);
          
          if (visibleUnreadIds.length > 0) {
              
              visibleUnreadIds.forEach(notificationId => {
                if (!readingTimers.value[notificationId]) {
                
                  readingProgress.value = {
                    ...readingProgress.value,
                    [notificationId]: 0
                  };
                
                  const progressInterval = setInterval(() => {
                    if (readingProgress.value[notificationId] < 100) {
                    
                      readingProgress.value = {
                        ...readingProgress.value,
                        [notificationId]: readingProgress.value[notificationId] + 1
                      };
                    } else {
                      clearInterval(progressInterval);
                    }
                  }, 100);
                  
                  readingTimers.value[notificationId] = setTimeout(() => {
                    const notificationIndex = notifications.value.findIndex(n => n.id === notificationId);
                    if (notificationIndex !== -1) {
                      const currentTime = new Date().toISOString();
                      notifications.value[notificationIndex].read = true;
                      notifications.value[notificationIndex].read_at = currentTime;
                    }
                    
                    markSingleAsRead(notificationId);
                    
                    clearInterval(progressInterval);
                    delete readingTimers.value[notificationId];
                    delete readingProgress.value[notificationId];
                  }, 10000); // 10 segundos
                }
              });
              
              markMultipleAsRead(visibleUnreadIds);
          }
       }, 10000);
    } else {
      Object.keys(readingTimers.value).forEach(id => {
        clearTimeout(readingTimers.value[id]);
        delete readingTimers.value[id];
        delete readingProgress.value[id];
      });
    }
  };

  const closeMenu = () => {
    showNotifications.value = false;
    Object.keys(readersMenu.value).forEach(key => {
        readersMenu.value[key] = false;
    });
    
    Object.keys(readingTimers.value).forEach(id => {
      clearTimeout(readingTimers.value[id]);
      delete readingTimers.value[id];
      delete readingProgress.value[id];
    });
  };
  
  const handleMouseEnter = (notification) => {
    if (!notification.read && !readingTimers.value[notification.id]) {
  
      readingProgress.value = {
        ...readingProgress.value,
        [notification.id]: 0
      };
      
      const progressInterval = setInterval(() => {
        if (readingProgress.value[notification.id] < 100) {
        
          readingProgress.value = {
            ...readingProgress.value,
            [notification.id]: readingProgress.value[notification.id] + 1
          };
        } else {
          clearInterval(progressInterval);
        }
      }, 100);
      
      readingTimers.value[notification.id] = setTimeout(() => {
        const notificationIndex = notifications.value.findIndex(n => n.id === notification.id);
        if (notificationIndex !== -1) {
          const currentTime = new Date().toISOString();
          notifications.value[notificationIndex].read = true;
          notifications.value[notificationIndex].read_at = currentTime;
        }
        markSingleAsRead(notification.id);
        
        clearInterval(progressInterval);
        delete readingTimers.value[notification.id];
        delete readingProgress.value[notification.id];
      }, 10000); // 10 segundos
    }
  };

  const handleItemClick = (notification) => {
    if (readingTimers.value[notification.id]) {
      clearTimeout(readingTimers.value[notification.id]);
      delete readingTimers.value[notification.id];
      delete readingProgress.value[notification.id];
    }
    
    if (!notification.read) {
      const notificationIndex = notifications.value.findIndex(n => n.id === notification.id);
      if (notificationIndex !== -1) {
        const currentTime = new Date().toISOString();
        notifications.value[notificationIndex].read = true;
        notifications.value[notificationIndex].read_at = currentTime;
      }
      markSingleAsRead(notification.id);
    }
    
    if (notification.link) {
      if (notification.link.startsWith('/')) {
          router.push(notification.link);
      } else {
          window.open(notification.link, '_blank');
      }
      closeMenu();
    }
    closeMenu();
  };
  
  const markMultipleAsRead = (notificationIds) => {
    if (socket.value && authStore.user?.id_user && notificationIds.length > 0) {
      console.log('Marking as read via WS:', notificationIds);
      notificationIds.forEach(notificationId => {
          const notificationIndex = notifications.value.findIndex(n => n.id === notificationId);
          if (notificationIndex !== -1 && !notifications.value[notificationIndex].read) {
              //  notifications.value[notificationIndex].read = true;
               notifications.value[notificationIndex].read_at = new Date().toISOString();
          }

          socket.value.emit('notification:mark_as_read', {
              notificationId,
              userId: authStore.user.id_user
          });
      });
    } else if (notificationIds.length > 0) {
       console.error('Socket not connected or user not logged in to mark multiple as read.');
       toast.error('Erro de conexão ao marcar notificações como lidas.');
    }
  };

  const markAllAsRead = async () => {
    const unreadIds = notifications.value.filter(n => !n.read).map(n => n.id);
    if (unreadIds.length === 0) return;

    const originalNotifications = JSON.parse(JSON.stringify(notifications.value));
    const currentTime = new Date().toISOString();
    
    notifications.value = notifications.value.map(n => {
      if (!n.read) {
        return { ...n, read: true, read_at: currentTime };
      }
      return n;
    });

    if (socket.value && authStore.user?.id_user) {
        socket.value.emit('notification:mark_all_as_read', {
            userId: authStore.user.id_user
        });
    } else {
        console.error('Socket not connected or user not logged in to mark all as read.');
        notifications.value = originalNotifications;
        toast.error('Erro de conexão ao marcar todas como lidas.');
    }
  };
  
  const filterByCategory = (category) => {
    activeFilter.value = category;
  };
  
  const filterByUrgency = (urgency) => {
    activeUrgencyFilter.value = urgency;
  };
  
  const getCategoryColor = (category) => {
    // Normalizar a categoria (remover espaços extras e converter para minúsculas)
    const normalizedCategory = typeof category === 'string' ? category.trim().toLowerCase() : '';
    
    switch (normalizedCategory) {
      case 'proposta': return 'blue lighten-1';
      case 'lista_tecnica': return 'green lighten-1';
      case 'checklist': return 'amber darken-2';
      case 'comunicado': return 'deep-purple lighten-1';
      default: return 'grey lighten-1';
    }
  };

  const getCategoryIcon = (category) => {
    // Normalizar a categoria (remover espaços extras e converter para minúsculas)
    const normalizedCategory = typeof category === 'string' ? category.trim().toLowerCase() : '';
    
    switch (normalizedCategory) {
      case 'proposta': return 'mdi-calendar-check';
      case 'lista_tecnica': return 'mdi-cogs';
      case 'checklist': return 'mdi-truck-delivery';
      case 'comunicado': return 'mdi-bullhorn';
      default: return 'mdi-bell';
    }
  };
  
  const getCategoryLabel = (category) => {
    // Normalizar a categoria (remover espaços extras e converter para minúsculas)
    const normalizedCategory = typeof category === 'string' ? category.trim().toLowerCase() : '';
    
    switch (normalizedCategory) {
      case 'proposta': return 'Proposta';
      case 'lista_tecnica': return 'Lista Técnica';
      case 'checklist': return 'Checklist';
      case 'comunicado': return 'Comunicado';
      default: return category || 'Sistema';
    }
  };
  
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Urgente': return 'red lighten-1';
      case 'Importante': return 'orange lighten-1';
      default: return 'blue-grey lighten-2'; // Cor mais neutra para Normal
    }
  };
  
  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'Urgente': return 'mdi-alert-decagram'; // Ícone mais chamativo
      case 'Importante': return 'mdi-alert-circle-outline';
      default: return 'mdi-information-outline';
    }
  };

  const formatNotificationMessage = (notification) => {
    if (notification.created_by === authStore.user?.id_user) {
      return notification.message.replace(/por\s+[^\s]+(\s|$)/i, 'por VOCÊ$1');
    }
    return notification.message;
  };

  async function fetchReaders(notificationId) {
    if (readersData.value[notificationId]?.readers || readersData.value[notificationId]?.loading) {
      return;
    }

    readersData.value = {
      ...readersData.value,
      [notificationId]: { loading: true, error: null, readers: [] }
    };

    try {
      const res = await http.post(`${ips.ipSecurity}notifications/${notificationId}/readers`);

      if (res && res.data) {
        let readersArray = [];
        if (Array.isArray(res.data.rows)) {
          readersArray = res.data.rows;
        }
        else if (Array.isArray(res.data)) {
          readersArray = res.data;
        }
        else if (typeof res.data === 'object' && res.data !== null && !Array.isArray(res.data)) {
          readersArray = Object.values(res.data);
        }
         else {
          console.warn("Formato inesperado para dados dos leitores:", res.data);
        }

         if(Array.isArray(readersArray)) {
            // Filtro anti-duplicidade aprimorado para leitores
            console.log(`Processando ${readersArray.length} leitores para notificação ${notificationId}`);
            
            const uniqueReaders = [];
            const seen = new Set();
            
            for (const reader of readersArray) {
              const userId = String(reader.user_id); // Normalizar como string
              if (!seen.has(userId)) {
                uniqueReaders.push({
                  ...reader,
                  formattedReadAt: moment(reader.read_at).fromNow()
                });
                seen.add(userId);
              } else {
                console.warn(`Leitor duplicado encontrado para user_id: ${userId} na notificação ${notificationId}`);
              }
            }
            
            console.log(`Após filtro: ${uniqueReaders.length} leitores únicos para notificação ${notificationId}`);
            readersData.value[notificationId].readers = uniqueReaders;
         } else {
            console.error("Could not process readers data into an array.");
            readersData.value[notificationId].readers = []; 
            readersData.value[notificationId].error = 'Falha ao processar dados.';
         }

      } else {
          readersData.value[notificationId].readers = []; // Handle case where res or res.data is null/undefined
      }
    } catch (err) {
      console.error(`Erro ao buscar leitores para notificação ${notificationId}:`, err);
      readersData.value[notificationId].error = 'Falha ao carregar leitores.';
    } finally {
       if (readersData.value[notificationId]) {
            readersData.value[notificationId].loading = false;
       }
    }
  }

  const markSingleAsRead = (notificationId) => {
    if (socket.value && authStore.user?.id_user) {
      socket.value.emit('notification:mark_as_read', {
          notificationId,
          userId: authStore.user.id_user
      });
    } else {
      console.error('Socket not connected or user not logged in to mark as read.');
      toast.error('Erro de conexão ao marcar como lida.');
    }
  };

  onMounted(() => {
    fetchNotifications();

    if (socket.value) {
      socket.value.connect();

      const registerUserWithRetry = (retryCount = 0) => {
        if (authStore.user?.id_user) {
          console.log('Attempting to register user in websocket:', authStore.user.id_user);
          socket.value.emit('notification:register_user', { userId: authStore.user.id_user });
          
          if (retryCount < 3) {
            setTimeout(() => {
              if (!userRegistered.value) {
                console.log(`Retrying websocket registration (attempt ${retryCount + 1})...`);
                registerUserWithRetry(retryCount + 1);
              }
            }, 2000);
          }
        } else {
          console.warn('User not logged in, cannot register WebSocket user.');
        }
      };
      
      registerUserWithRetry();

      // Registrar listeners apenas uma vez
      if (!listenersRegistered.value) {
        socket.value.on('notification:new', handleNewNotification);
        socket.value.on('notification:mark_read_ack', handleMarkReadAck);
        socket.value.on('notification:mark_all_read_ack', handleMarkAllReadAck);
        socket.value.on('notification:register_ack', handleRegisterAck);

        socket.value.on("connect_error", (err) => {
          console.error("WebSocket Connection Error:", err.message);
        });
        
        socket.value.on("connect", () => {
          console.log("WebSocket Connected:", socket.value.id);
          registerUserWithRetry();
        });
        
        listenersRegistered.value = true;
      }
    }
  });

  onUnmounted(() => {
    if (socket.value && listenersRegistered.value) {
      socket.value.off('notification:new', handleNewNotification);
      socket.value.off('notification:mark_read_ack', handleMarkReadAck);
      socket.value.off('notification:mark_all_read_ack', handleMarkAllReadAck);
      socket.value.off('notification:register_ack', handleRegisterAck);
      socket.value.off("connect_error");
      socket.value.off("connect");
      
      listenersRegistered.value = false;
      console.log('Listeners removidos e flag resetada');
    }
  });

  // Recarregar notificações quando a aba mudar
  watch(activeTab, async () => {
    if (showNotifications.value) {
      await fetchNotifications();
    }
  });

  </script>
  
  <style scoped>
  .notification-btn {
    position: relative;
  }
  
  .notification-card {
    width: 420px;
    max-height: 600px;
    overflow-y: auto;
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
    border-radius: 8px;
    display: flex;
    flex-direction: column;
  }

  .filter-toolbar {
     flex-shrink: 0;
     border-bottom: 1px solid #eee;
     background-color: #f9f9f9;
  }
  .urgency-toolbar {
     flex-shrink: 0;
     border-bottom: 1px solid #eee;
     background-color: #f9f9f9;
  }

  .tab-container {
    border-bottom: 1px solid #eee;
  }

  .filter-scroll-container {
    display: flex;
    overflow-x: auto;
    white-space: nowrap; 
    padding-bottom: 2px; 
    width: 100%;
  }

  .filter-toolbar .v-btn,
  .urgency-toolbar .v-btn {
    text-transform: capitalize;
    font-size: 0.8rem;
    margin: 0 2px;
  }
  
  .filter-toolbar .v-btn.active,
  .urgency-toolbar .v-btn.active {
    font-weight: bold;
    color: var(--v-primary-base, #1976d2);
    background-color: rgba(25, 118, 210, 0.1);
  }

  .v-list {
    padding: 0;
    flex-grow: 1;
    overflow-y: auto;
  }

  .notification-item {
    border-bottom: 1px solid #eee;
    padding: 12px 16px 28px 16px;
    position: relative;
    cursor: pointer;
    transition: background-color 0.2s ease-in-out;
    display: flex;
    align-items: flex-start;
  }

   .notification-item:hover {
     background-color: #f5f5f5;
   }
  
  .notification-item:last-child {
    border-bottom: none;
  }

  .unread-item {
     background-color: #e3f2fd;
   }

   .unread-item:hover {
     background-color: #bbdefb;
   }
  
  .unread-indicator {
    position: absolute;
    top: 50%;
    left: 6px;
    transform: translateY(-50%);
    width: 8px;
    height: 8px;
    background-color: var(--v-primary-base, #1976d2);
    border-radius: 50%;
    box-shadow: 0 0 6px var(--v-primary-base, #1976d2);
  }
  
  .notification-title {
    white-space: normal;
    word-wrap: break-word;
    line-height: 1.4;
    font-size: 0.9rem;
    font-weight: 500;
    color: #333;
  }
  
  .notification-time {
    color: #666;
    font-size: 0.75rem;
    margin-top: 2px;
  }
  
  .notification-tags {
    margin-top: 6px;
  }

  .notification-footer {
    border-top: 1px solid #eee;
    padding: 4px 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-shrink: 0;
    background-color: #f9f9f9;
  }

  .notification-card::-webkit-scrollbar, .v-list::-webkit-scrollbar {
    width: 6px;
  }
  
  .notification-card::-webkit-scrollbar-track, .v-list::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  .notification-card::-webkit-scrollbar-thumb, .v-list::-webkit-scrollbar-thumb {
    background: #aaa;
    border-radius: 3px;
  }
  
  .notification-card::-webkit-scrollbar-thumb:hover, .v-list::-webkit-scrollbar-thumb:hover {
    background: #888;
  }
  
  .filter-scroll-container::-webkit-scrollbar {
    height: 4px;
  }
  
  .filter-scroll-container::-webkit-scrollbar-thumb {
    background: #ccc;
  }
  .filter-scroll-container::-webkit-scrollbar-thumb:hover {
    background: #aaa;
  }

  .dark-theme {
    background-color: #1E1E1E;
    color: #e0e0e0;
  }
  
  .dark-theme .filter-toolbar,
  .dark-theme .urgency-toolbar,
  .dark-theme .notification-footer {
    background-color: #333;
    border-color: #444;
  }

  .dark-theme .notification-item {
    border-color: #444;
  }

  .dark-theme .notification-item:hover {
     background-color: #3f3f3f;
   }

  .dark-theme .unread-item {
     background-color: #1e3a5f;
   }

   .dark-theme .unread-item:hover {
     background-color: #2a528a;
   }

  .dark-theme .unread-indicator {
     background-color: var(--v-primary-lighten1, #64b5f6);
     box-shadow: 0 0 6px var(--v-primary-lighten1, #64b5f6);
   }
  
  .dark-theme .notification-title {
    color: #e0e0e0;
  }
  
  .dark-theme .notification-time {
    color: #aaa;
  }
  
  .dark-theme .v-btn {
    color: #e0e0e0;
  }
  
  .dark-theme .v-btn.active {
    color: var(--v-primary-lighten1, #64b5f6);
    background-color: rgba(100, 181, 246, 0.15);
  }
  
  .dark-theme .notification-card::-webkit-scrollbar-track, .dark-theme .v-list::-webkit-scrollbar-track {
    background: #333;
  }
  
  .dark-theme .notification-card::-webkit-scrollbar-thumb, .dark-theme .v-list::-webkit-scrollbar-thumb {
    background: #666;
  }
  
  .dark-theme .notification-card::-webkit-scrollbar-thumb:hover, .dark-theme .v-list::-webkit-scrollbar-thumb:hover {
    background: #888;
  }

  .dark-theme .filter-scroll-container::-webkit-scrollbar-thumb {
    background: #555;
  }
  .dark-theme .filter-scroll-container::-webkit-scrollbar-thumb:hover {
    background: #777;
  }

  /* Animação */
  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.5s;
  }
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }

  .readers-tooltip {
    z-index: 1000 !important;
  }

  .dark-theme .readers-tooltip {
    z-index: 1000 !important;
  }

  .read-status-icon-container {
    position: absolute;
    bottom: 8px;
    left: 24px;
    z-index: 1;
  }

  .reading-progress-container {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 4px;
    background-color: rgba(200, 200, 200, 0.3);
    overflow: hidden;
  }

  .reading-progress-bar {
    height: 100%;
    background-color: var(--v-primary-base, #1976d2);
    transition: width 0.1s linear;
  }

  .reading-time-remaining {
    position: absolute;
    right: 4px;
    bottom: 6px;
    font-size: 10px;
    color: var(--v-primary-base, #1976d2);
    background-color: rgba(255, 255, 255, 0.7);
    padding: 1px 4px;
    border-radius: 4px;
  }

  .dark-theme .reading-progress-container {
    background-color: rgba(50, 50, 50, 0.3);
  }

  .dark-theme .reading-progress-bar {
    background-color: var(--v-primary-lighten1, #64b5f6);
  }

  .dark-theme .reading-time-remaining {
    color: var(--v-primary-lighten1, #64b5f6);
    background-color: rgba(50, 50, 50, 0.7);
  }
  </style>